import React, {useEffect, useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, message as messageApi, Table, Tooltip, Tag} from 'antd';
import FilterArea, { DEFAULT_DSP_FILTER_OPTIONS, type DspFilterOptions } from './components/FilterArea';
import DspFormDrawer from './components/DspFormDrawer';
import {GET_DSP_MANAGEMENT_LIST, useDspManagementList} from '../../../graphql/queries/getAllDsps';
import {useSaveDspWithWarehouses} from './graphql/mutations/saveDspWithWarehouses';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';
import {showError} from 'utils/exceptionUtils';
import {DspType} from '../../../graphql/types/DspType';
import Loading from "../../../components/Loading";
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

const PageBoy: React.FC = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedDsp, setSelectedDsp] = useState<DspType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filteredData, setFilteredData] = useState<DspType[]>([]);
    const [filters, setFilters] = useState<DspFilterOptions>(DEFAULT_DSP_FILTER_OPTIONS);
    const {t} = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // GraphQL hooks
    const {allDsps, loading, error, refetch} = useDspManagementList();
    const {warehouseOptions, loading: warehousesLoading} = useWarehouseOptions();

    const [saveDspWithWarehouses, {loading: savingDsp}] = useSaveDspWithWarehouses();

    // Initialize filtered data when dspData changes
    useEffect(() => {
        setFilteredData(allDsps.filter(dsp => dsp.status === 1));
        setFilters(DEFAULT_DSP_FILTER_OPTIONS);
        setCurrentPage(1);
    }, [allDsps]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    // Handle GraphQL errors
    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error]);

    const handleFilter = () => {
        let result = [...allDsps];

        if (filters.selectedDspIdsForName && filters.selectedDspIdsForName.length > 0) {
            const idSet = new Set(filters.selectedDspIdsForName);
            result = result.filter((dsp) => dsp.id != null && idSet.has(String(dsp.id)));
        }

        if (filters.dspCode) {
            result = result.filter(dsp => dsp.dspCode?.includes(filters.dspCode));
        }

        if (filters.warehouseId !== 'all') {
            result = result.filter(dsp => dsp.warehouseIds.includes(Number(filters.warehouseId)));
        }

        if (filters.statusFilter !== 'all') {
            result = result.filter(dsp => dsp.status === Number(filters.statusFilter));
        }

        setFilteredData(result);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(DEFAULT_DSP_FILTER_OPTIONS);
        setFilteredData(allDsps.filter(dsp => dsp.status === 1));
        setCurrentPage(1);
    };

    // Show new DSP drawer
    const showNewDspDrawer = () => {
        setSelectedDsp(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show DSP details
    const showDspDetail = (dsp: DspType) => {
        setSelectedDsp(dsp);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle DSP form submission (create or update)
    const handleDspSubmit = async (values: DspType & {
        openSettlementConfig?: boolean;
        settlementTrackingPoint?: string;
        billingCycleConfiguration?: number;
        settlementMode?: number;
        billType?: number[];
        timeZoneIana?: string;
        billUnitType?: number;
    }) => {
        try {
            const dspInput = {
                id: values.id || undefined,
                name: values.name,
                fullName: values.fullName,
                dspCode: values.dspCode,
                contactName: values.contactName || undefined,
                contactPhone: values.contactPhone || undefined,
                contactEmail: values.contactEmail || undefined,
                operationEmail: values.operationEmail || undefined,
                status: values.status,
                paymentTermDays: values.paymentTermDays,
                exitDelayDays: values.exitDelayDays,
                warehouseIds: values.warehouseIds,
                openSettlementConfig: values.openSettlementConfig || false,
                settlementTrackingPoint: values.settlementTrackingPoint,
                billingCycleConfiguration: values.billingCycleConfiguration,
                settlementMode: values.settlementMode,
                billType: values.billType,
                timeZoneIana: values.timeZoneIana,
                billUnitType: values.billUnitType
            };

            const {data} = await saveDspWithWarehouses({
                variables: {input: dspInput},
                refetchQueries: [{query: GET_DSP_MANAGEMENT_LIST}]
            });

            if (drawerMode === 'create') {
                messageApi.success(t('central.dsp.messages.dspCreatedSuccessfully') as string);
            } else {
                messageApi.success(t('central.dsp.messages.dspUpdatedSuccessfully') as string);
            }

            setDrawerVisible(false);
            // 重新获取数据
            refetch();
            // 返回落库结果：新建时抽屉需要用 dspId 接着保存财务档案
            return data?.saveDspWithWarehouses;
        } catch (error: any) {
            showError(error);
            // 抛回抽屉，避免失败时仍被当作提交成功而自动关闭
            throw error;
        }
    };

    // Table column definitions
    const dspColumns: TableColumnsType<DspType> = [
        {
            title: t('central.dsp.columns.code'),
            dataIndex: 'dspCode',
            key: 'dspCode',
            width: 110,
            align: 'left' as const,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showDspDetail(record)}
                    style={{ paddingLeft: 0 }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('central.dsp.columns.companyName'),
            dataIndex: 'name',
            key: 'name',
            width: 160,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.dsp.columns.fullName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 200,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                </Tooltip>
            ),
        },
        {
            title: t('central.dsp.columns.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            width: 150,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.dsp.columns.phone'),
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 150,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.dsp.columns.warehouses'),
            dataIndex: 'id',
            key: 'warehouses',
            width: 200,
            align: 'left' as const,
            ellipsis: false,
            render: (dspId: string, record: DspType) => {
                const warehouseNames = record.warehouses.map(warehouse => warehouse.name).join(', ');
                return <span style={{ whiteSpace: 'nowrap' }}>{warehouseNames || '-'}</span>;
            }
        },
        {
            title: t('central.dsp.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'left' as const,
            render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'red'}>
                    {status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended')}
                </Tag>
            ),
        }
    ];

    if (loading) {
        return (
            <Loading spinning={loading}/>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            <FilterArea
                warehousesOptions={warehouseOptions}
                loading={warehousesLoading}
                filters={filters}
                setFilters={setFilters}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                <Button
                    type="primary"
                    onClick={showNewDspDrawer}
                    loading={savingDsp}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.new')}</span>
                </Button>
                <Button
                    type="primary"
                    onClick={handleFilter}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.filter')}</span>
                </Button>
                <Button
                    onClick={handleResetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.reset')}</span>
                </Button>
            </div>

            {/* DSP table */}
            <Table
                rowKey="id"
                columns={dspColumns}
                dataSource={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
             className="mb-4"/>
            <TablePaginationBar
                total={filteredData.length}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) setPageSize(size);
                }}
                onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* DSP form drawer (shared for create/edit) */}
            <DspFormDrawer
                warehousesOptions={warehouseOptions}
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleDspSubmit}
                dsp={selectedDsp}
                mode={drawerMode}
            />
        </div>
    );
};

const DspPage: React.FC = () => {
    return (
            <PageBoy/>
    );
};

export default DspPage;